import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { useHistory, useParams } from 'react-router-dom';

import { Mail, Lock } from '@mui/icons-material';
import { LocalizedLink } from '../../_shared/LocalizedLink';
import { Providers } from '../providers/Providers';
import { useAuth } from '../AuthenticationContext';
import { RoutePrams } from '../Authentication.model';
import { useConfig } from '../../ConfigContext';
import { SubmitButton } from '../../_shared/form/SubmitButton';
import { useGameTheme } from '../../GameThemeContext';

interface FormValues {
  email: string;
  password: string;
}

export function Signin(): JSX.Element {
  const { t } = useTranslation();
  const auth = useAuth();
  const config = useConfig();
  const history = useHistory();
  const params = useParams<RoutePrams>();
  const theme = useGameTheme();

  const initialValuesFormik: FormValues = {
    email: '',
    password: '',
  };
  const loginSchema = Yup.object().shape({
    password: Yup.string()
      .required('form.field-required'),
    email: Yup.string()
      .email('form.mail-invalid')
      .required('form.field-required'),
  });

  const handleSubmitForm = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    if (config.clientConfig.recaptchaEnabled) {
      const token = await window.grecaptcha.execute(
        config.serverConfig.website.recaptcha.clientId,
        { action: 'login' },
      );
      auth.signin({
        email: values.email,
        password: values.password,
      }, token);
    } else {
      auth.signin({
        email: values.email,
        password: values.password,
      });
    }

    setSubmitting(false);
  };

  useEffect(() => {
    if (params.gameId) {
      theme.setGameId(params.gameId);
    }
  }, [params]);

  return (
    <div className='auth-card-container card-bhvra'>
      <div className='row text-center'>
        <div className='col-12'>
          <h2 className='my-3' data-cy='login-title-text'>{t('auth.login.title')}</h2>
          <p className='my-3'>{t('auth.login.description')}</p>
        </div>

        <Providers textBtn='auth.login.sign-in-with' />
      </div>

      <hr className='hr-text' data-content={t('or')} />

      <div className='row justify-content-center mt-4'>
        <div className='col-12'>
          <Formik
            initialValues={initialValuesFormik}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={loginSchema}
            enableReinitialize
            onSubmit={handleSubmitForm}
          >
            {({ errors, touched, handleSubmit }) => (
              <Form className='form-light'>
                <Field name='email'>
                  {({ field }: FieldProps) => (
                    <div className='form-group'>
                      <label htmlFor='email'>{t('form.label.email')}</label>
                      <div className='input-group'>
                        <div className='input-group-prepend'>
                          <span className='input-group-text'>
                            <Mail className='icon' />
                          </span>
                        </div>
                        <input
                          name='email'
                          id='email'
                          placeholder={t('form.label.email')}
                          className={`form-control
                            ${(errors.email && touched.email && handleSubmit) ? 'is-invalid' : ''}`}
                          type='text'
                          {...field}
                          data-cy='email-input'
                        />
                      </div>

                      {errors.email && touched.email && handleSubmit ? (
                        <div className='form-error' data-cy='email-error-text'>{t(`${errors.email}`)}</div>
                      ) : null}
                    </div>
                  )}
                </Field>
                <Field name='password'>
                  {({ field }: FieldProps) => (
                    <div className='form-group'>
                      <label htmlFor='password'>{t('form.label.password')}</label>
                      <div className='input-group'>
                        <div className='input-group-prepend'>
                          <span className='input-group-text'>
                            <Lock className='icon' />
                          </span>
                        </div>
                        <input
                          name='password'
                          id='password'
                          placeholder={t('form.label.password')}
                          className={`form-control
                            ${(errors.password && touched.password && handleSubmit) ? 'is-invalid' : ''}`}
                          type='password'
                          {...field}
                          data-cy='password-input'
                        />
                      </div>
                    </div>
                  )}
                </Field>
                <div className='form-row'>
                  <div className='col'>
                    <span
                      className='float-right pt-2 font-weight-bold underline-dotted pointer'
                      onClick={() => history.push(params?.gameId
                        ? `/forgot-password/${params.gameId}` : '/forgot-password')}
                    >
                      <u data-cy='forgot-password-link'>
                        {t('auth.login.forgot-password')}
                      </u>
                    </span>

                  </div>
                </div>
                <SubmitButton name='sign-in' />
              </Form>
            )}
          </Formik>
        </div>
        <div className='col text-center'>
          <p>
            {t('auth.login.no-account')}
            <LocalizedLink
              className='font-weight-bold'
              to={params?.gameId
                ? `/auth/signup/${params.gameId}` : '/auth/signup'}
            >
              <u data-cy='signup-link'>{t('auth.login.sign-up-page')}</u>
            </LocalizedLink>
          </p>
        </div>
      </div>
    </div>
  );
}
